import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Folder, 
  FolderOpen, 
  MoreVertical, 
  Edit2, 
  Trash2, 
  Calendar,
  User,
  FileText,
  ArrowRight
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { sv } from "date-fns/locale";
import type { Database } from "@/integrations/supabase/types";

type FolderType = Database['public']['Tables']['folders']['Row'];

interface ModernFolderCardProps {
  folder: FolderType;
  assessmentCount?: number;
  subfolderCount?: number;
  isShared?: boolean;
  /** Namn på några av dokumenten i mappen, visas som förhandsvisning */
  documentNames?: string[];
  onOpen: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  className?: string;
}

export const ModernFolderCard: React.FC<ModernFolderCardProps> = ({
  folder,
  assessmentCount = 0,
  subfolderCount = 0,
  isShared = false,
  documentNames,
  onOpen,
  onEdit,
  onDelete,
  className
}) => {
  const hasContent = assessmentCount > 0 || subfolderCount > 0;

  return (
    <Card 
      className={cn(
        "group transition-all duration-300 cursor-pointer glassmorphism border border-border/50 hover:border-primary/30",
        "transform hover:scale-[1.02] hover:-translate-y-1 backdrop-blur-sm shadow-lg hover:shadow-xl",
        className
      )}
      onClick={onOpen}
    >
      <CardContent className="p-6 bg-gradient-to-br hero-dark-gradient">
        <div className="flex items-start justify-between">
          {/* Folder Icon & Main Content */}
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <div className="flex-shrink-0 p-3 rounded-xl bg-white/20 group-hover:bg-white/30 transition-all duration-300 backdrop-blur-sm border border-white/30">
              {hasContent ? (
                <FolderOpen className="h-6 w-6 text-on-hero" />
              ) : (
                <Folder className="h-6 w-6 text-on-hero/70" />
              )}
            </div>
            
            <div className="min-w-0 flex-1 space-y-3">
              {/* Title & Status */}
              <div className="flex items-center gap-3">
                <h3 className="font-semibold text-foreground truncate text-base">
                  {folder.name}
                </h3>
                {isShared && (
                  <span className="px-2 py-1 bg-white/20 text-on-hero text-xs rounded-md font-medium backdrop-blur-sm border border-white/30">
                    Delad
                  </span>
                )}
                {hasContent && (
                  <span className="px-2 py-1 bg-white/20 text-on-hero text-xs rounded-md font-medium backdrop-blur-sm border border-white/30">
                    Aktiv
                  </span>
                )}
              </div>
              
              {/* Description */}
              {folder.description && (
                <p className="text-sm text-on-hero/80 line-clamp-2 leading-relaxed">
                  {folder.description}
                </p>
              )}
              
              {/* Stats */}
              <div className="flex items-center gap-6 text-sm text-on-hero/80">
                {subfolderCount > 0 && (
                  <div className="flex items-center gap-2">
                    <Folder className="h-4 w-4" />
                    <span className="font-medium">{subfolderCount}</span>
                    <span>mappar</span>
                  </div>
                )}
                {assessmentCount > 0 && (
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span className="font-medium">{assessmentCount}</span>
                    <span>dokument</span>
                  </div>
                )}
              </div>

              {documentNames && documentNames.length > 0 && (
                <ul className="space-y-1 text-xs text-on-hero/80">
                  {documentNames.map((name, i) => (
                    <li key={`${name}-${i}`} className="truncate">
                      · {name}
                    </li>
                  ))}
                  {assessmentCount > documentNames.length && (
                    <li className="text-on-hero/60">
                      + {assessmentCount - documentNames.length} till
                    </li>
                  )}
                </ul>
              )}
              
              {/* Metadata */}
              <div className="flex items-center gap-4 text-xs text-on-hero/70">
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>
                    {formatDistanceToNow(new Date(folder.created_at), { 
                      addSuffix: true, 
                      locale: sv 
                    })}
                  </span>
                </div>
                {folder.created_by && (
                  <div className="flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5" />
                    <span className="truncate max-w-24">{folder.created_by}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 ml-4">
            <Button
              variant="ghost"
              size="sm"
              className="opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white/20 backdrop-blur-sm"
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
            >
              <ArrowRight className="h-4 w-4" />
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white/20 backdrop-blur-sm"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {onEdit && (
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(); }}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    Redigera
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <DropdownMenuItem 
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Ta bort
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};